import { createLucideIcon } from 'lucide-react';

/**
 * A diamond outline carrying an exclamation mark — the elevated-priority marker
 * (R10.8, UI spec §4.2, §4.5).
 *
 * ## Why this is authored here rather than imported
 *
 * Lucide has no `diamond-alert`: it ships `Diamond`, `DiamondMinus`,
 * `DiamondPlus` and `DiamondPercent`, and the alert variants only over the
 * circle, square, octagon and triangle shapes. So the icon is composed from
 * Lucide's own primitives instead of drawn by hand — the outer path is verbatim
 * the `diamond` node and the exclamation is verbatim the `circle-alert` glyph —
 * and it is built through `createLucideIcon`, the library's public factory. That
 * gives it the same 24×24 grid, `stroke-width: 2`, round caps and joins,
 * `size`/`strokeWidth`/`className` props and `lucide lucide-diamond-alert`
 * classes as every other icon in the app, so it is a real Lucide component in
 * every respect that matters (§9 requires a real SVG icon, never a Unicode
 * glyph) rather than a look-alike that would drift from the set.
 *
 * ## Why a diamond and not the star it replaces
 *
 * The star read as "favourite"/"rating" rather than "this needs attention", and
 * it was rendered filled, which made it the heaviest mark on a bar carrying the
 * least specific meaning. An outlined diamond with an exclamation states urgency
 * directly. It is **not** filled, for the obvious reason that a solid fill would
 * swallow the exclamation.
 *
 * Note the deliberate collision: the milestone marker is also a diamond (§4.4).
 * They are distinguishable by the exclamation and by where they live — milestones
 * only ever appear in the swimlane strip, this marker only ever inside a bar — and
 * the legend names both in words, which is what R10.6 and R18.8 actually require.
 */
export const DiamondAlert = createLucideIcon({
  name: 'diamond-alert',
  size: 24,
  node: [
    [
      'path',
      {
        d: 'M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0Z',
        key: 'diamond-alert-body',
      },
    ],
    ['path', { d: 'M12 8v4', key: 'diamond-alert-bar' }],
    ['path', { d: 'M12 16h.01', key: 'diamond-alert-dot' }],
  ],
});
